import { filter, kebabCase } from 'lodash'
import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'

import { getAccessToken, getSpaceId } from '@/store/contextSubscriber'

import DatasourceIcon from './components/DatasourceIcon'
import useDatasource from './hooks/useDatasource'
import { Datasource } from './interfaces'
import { defaultActions } from './utilities/constants'

export function DatasourceCatalog() {
  const history = useHistory()
  const [datasource, setDatasource] = useDatasource()
  useEffect(() => (datasource ? history.push('metadata') : undefined), [history, datasource])

  type Inputs = Pick<Datasource, 'id' | 'name' | 'version'> & { authTypes: string[] }

  const { register, watch, setValue, getFieldState, handleSubmit } = useForm<Inputs>()

  const name = watch('name')
  const version = watch('version')
  const authTypes = {
    basic: {
      type: 'object',
      properties: {
        auth: {
          enum: ['basic']
        },
        baseUrl: {
          type: 'string',
          title: 'baseUrl'
        },
        username: {
          type: 'string',
          title: 'Username',
          description: ''
        },
        password: {
          type: 'string',
          title: 'Password',
          format: 'password',
          description: ''
        }
      },
      required: ['username', 'password', 'baseUrl']
    },
    custom: {
      type: 'object',
      properties: {
        auth: {
          enum: ['custom']
        },
        baseUrl: {
          type: 'string',
          title: 'baseUrl'
        }
      }
    },
    oauth2: {
      type: 'object',
      properties: {
        auth: {
          enum: ['oauth2']
        },
        baseUrl: {
          type: 'string',
          title: 'baseUrl'
        },
        clientId: {
          type: 'string',
          title: 'Client ID',
          format: 'password'
        },
        clientSecret: {
          type: 'string',
          title: 'Client Secret',
          format: 'string'
        }
      },
      required: ['clientId', 'clientSecret']
    },
    apiKey: {
      type: 'object',
      properties: {
        auth: {
          enum: ['apiKey']
        },
        baseUrl: {
          type: 'string',
          title: 'baseUrl'
        },
        apiKey: {
          type: 'string'
        },
        headers: {
          type: 'object',
          'x-ui:widget': 'hidden',
          properties: {
            'api-key': {
              type: 'string',
              default: '${apiKey}'
            }
          }
        }
      },
      required: ['apiKey']
    },
    aws4: {
      type: 'object',
      properties: {
        auth: {
          enum: ['aws4']
        },
        service: {
          type: 'string',
          title: 'Service',
          description: ''
        },
        region: {
          type: 'string',
          title: 'Region',
          description: '',
          default: 'us-east-1'
        },
        baseUrl: {
          type: 'string',
          title: 'Base URL',
          description: '',
          default: 'https://${service}.${region}.amazonaws.com',
          'x-ui:widget': 'hidden'
        },
        accessKeyId: {
          type: 'string',
          title: 'Access Key ID',
          format: 'password',
          description: ''
        },
        secretAccessKey: {
          type: 'string',
          title: 'Secret Access Key',
          format: 'password',
          description: ''
        },
        sessionToken: {
          type: 'string',
          title: 'Session Token',
          format: 'password',
          description: ''
        }
      },
      required: ['service', 'region', 'accessKeyId', 'secretAccessKey']
    }
  }

  const datasourcesQuery = useQuery({
    queryKey: ['datasources'],
    queryFn: async () => {
      let response
      if (process.env.NODE_ENV === 'production') {
        response = await fetch(`/v3/appbuilder/spaces/${getSpaceId()}/datasources`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` }
        })
      } else {
        // modifing the url to account for when we are running the workgrid-datasource-api locally
        response = await fetch(`http://localhost:3001/api/v1/datasources`, {
          headers: {
            // local dev headers to satisfy schema
            'x-workgrid-space': 'spaceId',
            'x-workgrid-tenant': 'tenantId',
            'x-workgrid-user': 'userId'
          }
        })
      }
      return response.json() as Promise<Datasource[]>
    }
  })

  useEffect(() => {
    if (!name || !version) return
    if (getFieldState('id').isDirty) return

    setValue('id', kebabCase(`${name.toLowerCase()}-${version}`).replaceAll(/\bv-(\d)/g, 'v$1'))
  }, [getFieldState, setValue, name, version])

  const submitHandler: SubmitHandler<Inputs> = data => {
    setDatasource({
      ...data,
      schema: {
        type: 'object',
        properties: {
          auth: {
            type: 'string',
            title: 'Auth',
            enum: data.authTypes,
            // Set the default if there is only 1 authType
            default: data.authTypes.length === 1 ? data.authTypes[0] : undefined,
            // Hide the widget if there is only 1 authType
            'x-ui:widget': data.authTypes.length === 1 ? 'hidden' : undefined
          },
          ...(data.authTypes.includes('custom') && {
            expiresIn: { type: 'number', title: 'Auth token expires in', default: 3600, 'x-ui:widget': 'hidden' }
          })
        },
        dependencies: {
          auth: {
            oneOf: filter(authTypes, (value, key) => data.authTypes.includes(key))
          }
        }
      },
      actions: defaultActions(data.authTypes),
      events: {}
    })
  }

  const onSelectDatasource = (datasource: Datasource) => {
    setDatasource(datasource)
    history.push('metadata')
  }

  return (
    <div className="min-h-full bg-zinc-50">
      <div className="bg-white pb-32">
        <header className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold tracking-tight">Workgrid Data Sources</h1>
            <p className="mt-2 max-w-4xl text-sm text-gray-500">
              Choose to edit one of the existing catalog datasources or create your own from scatch
            </p>
          </div>
        </header>
      </div>

      <main className="-mt-32">
        <div className="mx-auto max-w-7xl pb-12 lg:px-8">
          <div className="rounded-lg bg-white px-5 py-6 shadow sm:px-6">
            {/* Left column */}
            <div>
              <div className="flex">
                <div className="w-2/3">
                  <ul className="grid w-full list-none grid-cols-1 gap-x-4 gap-y-4 py-5 px-6 lg:grid-cols-2 xl:grid-cols-3">
                    {datasourcesQuery.isLoading
                      ? [0, 1, 2].map(i => (
                          <div key={i} className="w-[200px] rounded bg-white p-4 shadow">
                            <div className="flex animate-pulse space-x-4">
                              <div className="h-12 w-12 rounded-full bg-gray-200" />
                              <div className="flex-1 space-y-4 py-1">
                                <div className="h-2 rounded bg-gray-200" />
                                <div className="h-2 w-3/4 rounded bg-gray-200" />
                              </div>
                            </div>
                          </div>
                        ))
                      : datasourcesQuery.data?.map(datasource => (
                          <button
                            title={datasource.id}
                            key={datasource.id}
                            className="relative flex w-full cursor-pointer overflow-hidden rounded-lg border bg-white p-4 text-left shadow ring-offset-2 hover:ring hover:ring-blue-300"
                            onClick={() => onSelectDatasource(datasource)}
                          >
                            <div className="flex w-full flex-col justify-between">
                              <div className="flex w-full justify-between">
                                <div className="flex w-full items-start space-x-3">
                                  <div className={'flex'}>
                                    <DatasourceIcon src={datasource.iconUrl} size="sm" appName={datasource.name} />
                                    <div className="ml-2 flex w-full flex-col">
                                      <div className="flex w-full items-center">
                                        <div className="item-center flex w-full space-x-3">
                                          <span className="text-wrap text-md flex-wrap items-center font-bold">
                                            {datasource.name}
                                          </span>
                                        </div>
                                      </div>
                                      <p className="text-nowrap w-[130px] truncate pt-1 text-sm text-zinc-500">
                                        {datasource.id}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                  </ul>
                </div>
                <div className="mr-4 flex items-center py-3 text-xs uppercase text-gray-400">
                  <div className="h-full flex-1 border-l border-gray-200"></div>
                </div>
                {/* Right column */}
                <div className="w-1/3 p-2">
                  <section aria-labelledby="section-2-title">
                    <h2 className="sr-only" id="section-2-title">
                      Section title
                    </h2>
                    <div className="overflow-hidden rounded-lg bg-white shadow">
                      <div className="p-2">
                        <div className="flex min-h-full flex-1 flex-col justify-center py-4 sm:px-6 lg:px-8">
                          <div className="sm:mx-auto sm:w-full sm:max-w-md">
                            <h2 className="mt-2 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                              Create a Data Source
                            </h2>
                          </div>

                          <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
                            <form className="space-y-6" onSubmit={handleSubmit(submitHandler)}>
                              <div>
                                <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                                  Name
                                </label>
                                <div className="mt-2">
                                  <input
                                    id="name"
                                    type="text"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                    placeholder="ServiceNow"
                                    {...register('name', { required: true })}
                                  />
                                </div>
                              </div>

                              <div>
                                <label htmlFor="version" className="block text-sm font-medium leading-6 text-gray-900">
                                  Version
                                </label>
                                <div className="mt-2">
                                  <input
                                    id="version"
                                    type="text"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                    defaultValue="v1"
                                    {...register('version', { required: true })}
                                  />
                                </div>
                              </div>

                              <div>
                                <label htmlFor="id" className="block text-sm font-medium leading-6 text-gray-900">
                                  Slug
                                </label>
                                <div className="mt-2">
                                  <input
                                    id="id"
                                    type="text"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                    {...register('id', { required: true })}
                                  />
                                </div>
                              </div>
                              <div>
                                <label
                                  htmlFor="authTypes"
                                  className="block text-sm font-medium leading-6 text-gray-900"
                                >
                                  Auth Types
                                </label>
                                <div className="mt-2">
                                  <select
                                    id="authTypes"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                    {...register('authTypes', { required: true })}
                                    multiple
                                  >
                                    {Object.keys(authTypes).map(authType => (
                                      <option value={authType} key={authType}>
                                        {authType}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              <div>
                                <button
                                  type="submit"
                                  className="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                >
                                  Create
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
